import { Link, useLocation } from "react-router-dom";
import { useLocale } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { navigation, labels } from "@/data/content";
import { Phone, Calendar, Menu, ExternalLink } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { MobileNav } from "./MobileNav";
const martinezLogo = "/martinez-logo-sm.webp";

export function Header() {
  const { locale, t, getLocalizedHref } = useLocale();
  const location = useLocation();
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const megaRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (megaRef.current && !megaRef.current.contains(e.target as Node) && triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setMegaMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (href: string, esHref?: string) => {
    const path = location.pathname;
    return path === href || (esHref && path === esHref) || (href !== "/" && href !== "/es" && path.startsWith(href));
  };

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <header className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-background/98 backdrop-blur-xl border-b border-border/50 shadow-lg shadow-background/20"
          : "bg-background/85 backdrop-blur-lg border-b border-border/20"
      )}>
        {/* Utility Bar */}
        <div className={cn(
          "hidden lg:block border-b transition-all duration-500",
          scrolled ? "border-border/30 bg-secondary/20" : "border-transparent bg-secondary/10"
        )}>
          <div className="container mx-auto max-w-7xl px-6 md:px-8 lg:px-10 flex items-center justify-end gap-6 py-3 text-base">
            <LanguageSwitcher variant="compact" />
            <Link to={getLocalizedHref("/client-portal")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ExternalLink className="h-4 w-4" />
              {t(labels.clientPortal)}
            </Link>
            <a href="tel:+17738183020" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <Phone className="h-4 w-4" />
              (773) 818-3020
            </a>
            <Link to={getLocalizedHref("/contact")} className="inline-flex items-center gap-2.5 bg-ivory-cta text-ivory-cta-foreground px-6 py-2.5 rounded-md text-base font-semibold hover:bg-primary hover:text-primary-foreground transition-colors pulse-gold">
              <Calendar className="h-4.5 w-4.5" />
              {t(labels.bookConsultation)}
            </Link>
          </div>
        </div>

        {/* Main Nav */}
        <div className="container mx-auto max-w-7xl px-6 md:px-8 lg:px-10">
          <nav className="flex items-center justify-between h-[5rem] lg:h-[6rem]" aria-label="Main navigation">
            <Link to={locale === "es" ? "/es" : "/"} className="flex items-center">
              <img src={martinezLogo} alt="Martinez Law" className="h-14 lg:h-16 w-auto" width="250" height="112" fetchPriority="high" />
            </Link>

            <div className="hidden lg:flex items-center gap-1.5">
              {navigation.map((item) => {
                const active = isActive(item.href, item.esHref);
                if (item.children) {
                  const childActive = item.children.some(c => isActive(c.href, c.esHref));
                  return (
                    <div key={item.href} className="relative">
                      <button
                        ref={triggerRef}
                        onClick={() => setMegaMenuOpen(!megaMenuOpen)}
                        className={cn(
                          "px-5 py-3 text-[1.05rem] font-medium transition-colors relative",
                          megaMenuOpen || childActive
                            ? "text-primary"
                            : "text-foreground/90 hover:text-primary",
                          (megaMenuOpen || childActive) && "after:absolute after:bottom-0 after:left-5 after:right-5 after:h-[2px] after:bg-primary after:rounded-full"
                        )}
                        aria-expanded={megaMenuOpen}
                        aria-haspopup="true"
                      >
                        {t(item.label)}
                      </button>
                      {megaMenuOpen && (
                        <div
                          ref={megaRef}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[680px] bg-card/95 backdrop-blur-xl border border-border/50 rounded-lg shadow-2xl shadow-background/40 p-8 grid grid-cols-2 gap-2 animate-fade-in"
                          role="menu"
                        >
                          {item.children.map((child) => {
                            const childIsActive = isActive(child.href, child.esHref);
                            return (
                              <Link
                                key={child.href}
                                to={getLocalizedHref(child.href, child.esHref)}
                                onClick={() => setMegaMenuOpen(false)}
                                className={cn(
                                  "flex items-center gap-2 px-5 py-3.5 text-[1.05rem] rounded-md transition-all",
                                  "border-l-2",
                                  childIsActive
                                    ? "text-primary border-primary bg-primary/5"
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 border-transparent hover:border-primary/40"
                                )}
                                role="menuitem"
                              >
                                {t(child.label)}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.href}
                    to={getLocalizedHref(item.href, item.esHref)}
                    className={cn(
                      "px-5 py-3 text-[1.05rem] font-medium transition-colors relative",
                      active
                        ? "text-primary after:absolute after:bottom-0 after:left-5 after:right-5 after:h-[2px] after:bg-primary after:rounded-full"
                        : "text-foreground/90 hover:text-primary"
                    )}
                  >
                    {t(item.label)}
                  </Link>
                );
              })}
            </div>

            <div className="flex items-center gap-3 lg:hidden">
              <LanguageSwitcher variant="compact" />
              <button
                onClick={() => setMobileOpen(true)}
                className="p-2.5 text-foreground/80 hover:text-foreground transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-7 w-7" />
              </button>
            </div>
          </nav>
        </div>
      </header>

      <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
